import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [issueType, setIssueType] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Support request submitted! Ticket #HL360-4892 created.');
    setIssueType('');
    setMessage('');
  };

  const faqs = [
    {
      question: 'How do I add a new member to the platform?',
      answer: 'Click the "Add New Member" button on the Dashboard or Members page. Fill in the required fields including name, contact information, date of birth, location, and health conditions. Assign a provider and the member will receive an automated setup email.',
    },
    {
      question: 'What do the risk scores (1-5) mean?',
      answer: 'Risk scores help prioritize member care: 1-2 (Critical/High - requires immediate attention), 3 (Monitor closely - proactive care needed), 4-5 (Good/Excellent - routine care). The system automatically calculates risk based on biometric trends and missed check-ins.',
    },
    {
      question: 'How is the Digital Twin Score calculated?',
      answer: 'The Digital Twin Score (DTS) is a composite score (0-100) based on multiple health metrics: heart rate, heart rate variability (HRV), sleep quality, SpO2 levels, daily activity (steps), and mood. The score is updated daily as new biometric data syncs from wearable devices.',
    },
    {
      question: 'How do I export member data?',
      answer: 'From the Member Detail view, click "Download Full Report" to generate a PDF. From the Members page, click "Export" to download a CSV file. All exports are HIPAA-compliant and encrypted for security.',
    },
    {
      question: 'What if a member\'s device stops syncing?',
      answer: 'Check the "Last Sync" timestamp in the member details. If it\'s been over 24 hours, the member may need to reconnect their device via the mobile app Settings. You can also send them a reminder notification from the admin portal.',
    },
    {
      question: 'Can I customize risk score thresholds?',
      answer: 'Yes, system administrators can adjust risk thresholds in Settings > Risk Configuration. Changes apply to all providers in your organization. Contact support if you need assistance with custom configurations.',
    },
  ];

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="help-title">
          Help & Support Center
        </h1>
        <p className="text-gray-600 mt-1">Find answers and get assistance</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="help-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* FAQs */}
      <Card data-testid="faq-card">
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} data-testid={`faq-${index}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>
                  <p className="text-gray-700">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card data-testid="contact-support-card">
        <CardHeader>
          <CardTitle>Contact Technical Support</CardTitle>
          <p className="text-sm text-gray-600 mt-1">For urgent technical issues, clinical questions, or platform assistance</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="issue-type">Issue Type *</Label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger data-testid="issue-type-select">
                  <SelectValue placeholder="Select issue type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">Technical Issue (Login, Performance, Data Sync)</SelectItem>
                  <SelectItem value="member">Member Management Question</SelectItem>
                  <SelectItem value="billing">Billing or Account Question</SelectItem>
                  <SelectItem value="feature">Feature Request</SelectItem>
                  <SelectItem value="security">Security Concern</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                placeholder="Describe your issue or question..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                required
                data-testid="support-message"
              />
            </div>

            <Button
              type="submit"
              className="rounded-full"
              style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
              data-testid="submit-support-btn"
            >
              Submit Support Request
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
            <div className="flex items-center space-x-3 text-sm">
              <Phone className="w-5 h-5" style={{ color: '#8F2FDF' }} />
              <div>
                <p className="font-medium">Emergency Hotline</p>
                <p className="text-gray-600">1-800-HEALTH-360</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <Clock className="w-5 h-5" style={{ color: '#8F2FDF' }} />
              <div>
                <p className="font-medium">Support Hours</p>
                <p className="text-gray-600">Mon-Fri 8AM-8PM ET, Sat-Sun 10AM-6PM ET</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <Mail className="w-5 h-5" style={{ color: '#8F2FDF' }} />
              <div>
                <p className="font-medium">Response Time</p>
                <p className="text-gray-600">Within 2 hours during business hours</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Help;
